import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import dayjs from "dayjs";
import Toaster from "../../components/toaster";
import { useAppDispatch } from "../../helpers/hooks";
import SuccessIcon from "../../static/images/success-icon-outlined.svg";
import FailedIcon from "../../static/images/failed_icon.svg";
import FileIcon from "../../static/images/file_icon.svg";
import RetryIcon from "../../static/images/retry_icon.svg";
import {
  fetchAllEventsList,
  AllEventsResponse,
  Event as AllEventsApiEvent,
} from "../../redux/slices/allEventslistSlice";
import CustomPagination from "../common/CustomPagination";
import { generateEventCertificate } from "../profile/userProfileSlice";
import {
  fetchEventCertificates,
  VolunteerCertificate,
} from "../../redux/slices/eventCertificateSlice";
import CommonSelect from "../../components/commonSelect";
import { sendEmailToParticipants } from "../eventDetails/eventDetailsSlice";
import LoadingButton from "@mui/lab/LoadingButton";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface IUpdatedUser {
  userId: number;
  hours: number;
}
export interface EventOption {
  eventId: string;
  eventName: string;
}

interface props {
  allUserData: any;
  onToast: (type: "success" | "error" | "info", message: string) => void;
}

const ITEMS_PER_PAGE = 5;

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pb: 3 }}>{children}</Box>}
    </div>
  );
}

const EventCertificateGenerate = ({ allUserData, onToast }: props) => {
  const dispatch = useAppDispatch();
  const [selectedRegisteredUsers, setSelectedRegisteredUsers] = useState<
    VolunteerCertificate[]
  >([]);
  const [value, setValue] = React.useState(0);
  const [userData, setUserData] = useState<VolunteerCertificate[] | null>(null);
  const [openToaster, setOpenToaster] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastType, setToastType] = React.useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [pageNo, setPageNo] = React.useState(1);
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [allEvents, setAllEvents] = useState<EventOption[]>([]);
  const formatDate = (date: any) => {
    let istDate = dayjs(date).add(330, "minute");
    let datestring = dayjs(istDate, "YYYY-MM-DD HH:mm:ss").format(
      "DD MMM, YYYY hh:mm a",
    );
    return datestring;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const raw = (await dispatch(
          fetchAllEventsList("completed"),
        ).unwrap()) as AllEventsResponse | AllEventsApiEvent[];

        const list: AllEventsApiEvent[] = Array.isArray(raw)
          ? raw
          : (raw.completed ?? []);

        const data: EventOption[] = list.map((event) => ({
          eventId: String(event.eventId),
          eventName: event.eventName,
        }));

        setAllEvents(data);
      } catch (error) {
        console.error("Error fetching token", error);
      }
    };
    fetchEvents();
  }, []);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allUsers = userData || [];
      setSelectedRegisteredUsers(allUsers);
    } else {
      setSelectedRegisteredUsers([]);
    }
  };

  const handleRowSelect = (email: string) => {
    const user = userData?.find((u: any) => u.email === email);
    if (!user) return;

    setSelectedRegisteredUsers((prev) => {
      const exists = prev.some((u: any) => u.email === email);
      return exists
        ? prev.filter((u: any) => u.email !== email)
        : [...prev, user];
    });
  };

  const isAllSelected =
    (userData?.length ?? 0) > 0 &&
    selectedRegisteredUsers.length === userData?.length;

  const handleGenerateCertificate = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await dispatch(sendEmailToParticipants(selectedEvent)).unwrap();
      onToast("success", "Mail sent successfully to the participants!");
    } catch (err: any) {
      console.error(err);
      onToast(
        "error",
        err?.response?.data || err?.message || "Something went wrong!",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // To close toast message
  const handleToasterClose = () => {
    setOpenToaster(false);
  };

  const handleEventChange = async (eventId: string) => {
    setUserData(null);
    setSelectedEvent(eventId);
    setPageNo(1);
    setIsLoading(true);

    try {
      const response = await dispatch(fetchEventCertificates(eventId)).unwrap();
      setUserData(response.length > 0 ? response : null);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const allEventOptions = useMemo(() => {
    return allEvents?.map((event) => ({
      label: event.eventName,
      value: String(event.eventId),
    }));
  }, [allEvents]);

  async function handleOpenCertificate(volunteer: VolunteerCertificate) {
    if (selectedEvent === "") {
      onToast("error", "Please select an event");
      return;
    }

    try {
      const registerRes = await dispatch(
        generateEventCertificate({
          eventId: selectedEvent,
          shortId: volunteer.shortId,
        }),
      );
      if (registerRes.meta.requestStatus === "fulfilled") {
        let url = process.env.REACT_APP_API_URL;
        url = registerRes?.payload[0];
        window.open(url, "_blank");
      } else {
        const errorMessage: string =
          registerRes.payload?.response?.data || "Something went wrong!";
        onToast("error", errorMessage);
      }
    } catch (error) {
      onToast("error", "An unexpected error occurred");
    }
  }

  const paginatedVolunteers = userData?.slice(
    (pageNo - 1) * ITEMS_PER_PAGE,
    pageNo * ITEMS_PER_PAGE,
  );

  return (
    <>
      <Box sx={{ backgroundColor: "#fff", borderRadius: 1, boxShadow: 1 }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#000",
            color: "#fff",
            px: 3,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography variant="h5" fontWeight={400}>
            Event Certificates
          </Typography>

          {/* Select Event */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CommonSelect
              searchable
              placeholder="Select Event"
              options={allEventOptions}
              value={selectedEvent}
              borderColor="#fff"
              onChangeHandler={handleEventChange}
              sx={{
                width: 200,
                borderRadius: "4px",
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": {
                  height: 45,
                },
              }}
            />
          </Box>
        </Box>

        {!userData ? (
          <Box py={6} display="flex" flexDirection="column" alignItems="center">
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              No users to display
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                maxWidth: 400,
                lineHeight: 1.6,
              }}
            >
              Select an event or different event to view users here.
            </Typography>
          </Box>
        ) : (
          <CustomTabPanel value={value} index={0}>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                  <TableRow>
                    {/* <TableCell padding="checkbox">
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          indeterminate={
                            selectedRegisteredUsers.length > 0 &&
                            selectedRegisteredUsers.length < userData?.length
                          }
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                        <Typography fontWeight={600}>All</Typography>
                      </Box>
                    </TableCell> */}
                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Name
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Dept.
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Email
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Date
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                        Action
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                
                  <TableBody>
                    {paginatedVolunteers?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No users to display.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedVolunteers?.map((listItem: VolunteerCertificate) => (
                        <TableRow key={listItem.shortId}>
                          {/* <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRegisteredUsers.some(
                                (u: any) => u.email === listItem.email,
                              )}
                              onChange={() => handleRowSelect(listItem.email)}
                            />
                          </TableCell> */}
                          <TableCell>{listItem.name}</TableCell>
                          <TableCell>{listItem.dept}</TableCell>
                          <TableCell>{listItem.email}</TableCell>
                          <TableCell>{formatDate(listItem.date)}</TableCell>
                          <TableCell
                            sx={{
                              color: listItem.status ? "green" : "red",
                              fontWeight: 600,
                            }}
                          >
                            {listItem.status ? (
                              <Avatar
                                sx={{
                                  width: "26%",
                                  height: "26%",
                                }}
                                src={SuccessIcon}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  width: "26%",
                                  height: "26%",
                                  alignSelf: "center",
                                }}
                                src={FailedIcon}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {!listItem.status ? (
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <Button
                                  variant="text"
                                  sx={{
                                    borderRadius: "2px",
                                    borderWidth: 1,
                                    fontWeight: 700,
                                    textTransform: "none",
                                  }}
                                  onClick={() =>
                                    handleOpenCertificate(listItem)
                                  }
                                  disabled={false}
                                  title="retry"
                                >
                                  <img
                                    height={20}
                                    width={20}
                                    src={RetryIcon}
                                    alt="retry icon"
                                  />
                                </Button>
                              </Box>
                            ) : (
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <Button
                                  variant="text"
                                  sx={{
                                    borderRadius: "2px",
                                    borderWidth: 1,
                                    fontWeight: 700,
                                    textTransform: "none",
                                  }}
                                  onClick={() =>
                                    handleOpenCertificate(listItem)
                                  }
                                  disabled={false}
                                  title="open certificate"
                                >
                                  <img
                                    height={20}
                                    width={20}
                                    src={FileIcon}
                                    alt="retry icon"
                                  />
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                
              </Table>
            </TableContainer>

            {/* ACTION BUTTONS */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Box sx={{ flex: 1 }} />

              <Box sx={{ display: "flex", gap: 2 }}>
                <LoadingButton
                  variant="contained"
                  loading={isLoading}
                  loadingIndicator="loading..."
                  sx={{
                    borderRadius: "2px",
                    borderWidth: 1,
                    color: "#FFF",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                  onClick={handleGenerateCertificate}
                  disabled={!selectedEvent}
                >
                  Generate Certificate
                </LoadingButton>
              </Box>

              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <CustomPagination
                  totalItems={userData?.length ?? 0}
                  currentPage={pageNo}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setPageNo}
                />
              </Box>
            </Box>
          </CustomTabPanel>
        )}

        {/* TOASTER */}
        <Toaster
          openToaster={openToaster}
          toastMessage={toastMessage}
          type={toastType}
          handleToasterClose={handleToasterClose}
        />
      </Box>
    </>
  );
};
export default React.memo(EventCertificateGenerate);
